import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Contract, ContractStatus, WelcomeEmailStatus,AdobeStatus } from '@/types';
import { createContract as apiCreateContract, getContracts as apiFetchContracts, updateContract as apiUpdateContract, deleteContract as apiDeleteContract } from '@/api/contracts';
import { generateAgreement as apiGenerateAgreement } from '@/api/agreements';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { formatErrorMessage } from '@/utils/errorHandling';

type ContractContextType = {
    contracts: Contract[];
    pagination: {
        currentPage: number;
        totalPages: number;
        pageSize: number;
        total: number;
    };
    addContract: (contract: Omit<Contract, '_id'>) => Promise<void>;
    updateContract: (contractId: number, updatedData: Partial<Contract>) => Promise<void>;
    updateContractStatus: (contractId: number, status: string, updatedData?: Partial<Contract>) => void;
    updateContractBookingsStatus: (contractId: number, status: string, updatedData?: Partial<Contract>) => void;
    updateContractBookingStatus: (contractId: number, bookingId: number, status: string, updatedData?: Partial<Contract>) => void;
    removeBooking: (contractId: number, bookingId: number) => void;
    deleteContract: (contractId: number) => Promise<void>;
    fetchContracts: (params?: { size?: number; page?: number; search?: string }) => Promise<void>;
    generateAgreement: (contractId: number, isTestSubmission: boolean, isSendWelcomeEmail: boolean, isSendWelcomeEmailDraft?: boolean, isAttachJointMemberForm?: boolean) => Promise<boolean>;
    isLoading: boolean;
};

const ContractContext = createContext<ContractContextType | undefined>(undefined);

export function ContractProvider({ children }: { children: React.ReactNode }) {
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        pageSize: 50,
        total: 0
    });
    const [isLoading, setIsLoading] = useState(false);
    const [fetchCount, setFetchCount] = useState(0);
    const { user, isLoading: isAuthLoading } = useAuth();

    // Use memoized fetchContracts function to prevent recreating the function on each render
    const fetchContracts = useCallback(async (params?: { size?: number; page?: number; search?: string }) => {
        try {
            setIsLoading(true);
            const response = await apiFetchContracts(params);

            if (response && response.success) {
                // Ensure we handle the data correctly based on the API response
                setContracts(response.contracts || []);
                
                // Update pagination data from response meta
                if (response.meta) {
                    setPagination({
                        currentPage: response.meta.current_page || 1,
                        totalPages: response.meta.last_page || 1,
                        pageSize: response.meta.per_page || 10,
                        total: response.meta.total || 0
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching contracts:', error);
            toast.error('Failed to fetch contracts');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Only fetch contracts once when the component mounts and user is authenticated
    useEffect(() => {
        if (!isAuthLoading && user && fetchCount === 0) {
            fetchContracts();
            setFetchCount(prev => prev + 1);
        }
    }, [isAuthLoading, user, fetchContracts, fetchCount]);

    const addContract = async (contract: Omit<Contract, '_id'>) => {
        try {
            setIsLoading(true);

            const contractWithDefaults = {
                ...contract,
                status: ContractStatus.New,
                weclomeEmailStatus: contract.weclomeEmailStatus || WelcomeEmailStatus.Pending, // Default to "pending"
                // isNoBookingRequired: contract.isNoBookingRequired || false,
                archiveStatus: contract.archiveStatus || 'pending', // Ensure archiveStatus is set
            };

            const response = await apiCreateContract(contractWithDefaults);

            if (response && response.success) {
                const newContract = response.contract as Contract;
                setContracts((prev) => [...prev, newContract]);
                toast.success("Contract added successfully");
            } else {
                throw new Error(response?.message || "Failed to add contract");
            }
        } catch (error) {
            console.error("Error adding contract:", error);
            toast.error(error.message || "Failed to add contract");
        } finally {
            setIsLoading(false);
        }
    };

    const updateContract = async (contractId: number, updatedData: Partial<Contract>) => {
        try {
            setIsLoading(true);

            // Ensure bookings is always an array and remove empty objects
            const bookings = (updatedData.bookings || [])
                .filter(booking => booking && Object.keys(booking).length > 0);

            // // Ensure isNoBookingRequired is properly set
            // const isNoBookingRequired = updatedData.isNoBookingRequired !== undefined
            //     ? updatedData.isNoBookingRequired
            //     : contracts.find(c => c._id === contractId)?.isNoBookingRequired || false;

            // Ensure archiveStatus is properly set
            const archiveStatus = updatedData.archiveStatus !== undefined
                ? updatedData.archiveStatus
                : contracts.find(c => c._id === contractId)?.archiveStatus || 'pending';

            // Determine if the contract status should be changed to "pending"
            let newStatus = updatedData.status;

            // // If isNoBookingRequired is true or there are bookings, set status to "pending"
            // if (isNoBookingRequired || bookings.length > 0) {
            //     newStatus = ContractStatus.Pending;

            // } else if (!newStatus) {
            //     // If no status is provided and there's no reason to change to pending, use current or default to "new"
            //     const currentContract = contracts.find(c => c._id === contractId);
            //     newStatus = currentContract?.status || ContractStatus.New;
            // }

            const contractWithDefaults = {
                ...updatedData,
                bookings,
                //isNoBookingRequired,
                status: newStatus,
                weclomeEmailStatus: updatedData.weclomeEmailStatus || WelcomeEmailStatus.Pending, // Default to "pending"
                archiveStatus: archiveStatus, // Ensure archiveStatus is included
            };

            // Call the API to update the contract
            const response = await apiUpdateContract(contractId, contractWithDefaults);

            if (response && response.success) {
                // Update the contracts state with the updated contract
                setContracts(prev => {
                    return prev.map(contract => {
                        if (contract._id === contractId) {
                            return {
                                ...contract,
                                ...contractWithDefaults
                            };
                        }
                        return contract;
                    });
                });

                toast.success("Contract updated successfully");
                
            } else {
                if (response?.errors) {
                    // Handle validation errors
                    if (typeof response.errors === 'object' && response.errors !== null) {
                        // Extract error messages from the object
                        const errorMessages: string[] = [];
                        Object.entries(response.errors).forEach(([field, messages]) => {
                            if (Array.isArray(messages)) {
                                messages.forEach(message => {
                                    errorMessages.push(`${field}: ${message}`);
                                });
                            } else if (typeof messages === 'string') {
                                errorMessages.push(`${field}: ${messages}`);
                            }
                        });

                        if (errorMessages.length > 0) {
                            // Show each validation error as a separate toast
                            errorMessages.forEach(message => {
                                toast.error(message);
                            });
                            throw new Error("Validation failed");
                        }
                    } else if (Array.isArray(response.errors)) {
                        // Handle array of errors
                        response.errors.forEach(message => {
                            toast.error(message);
                        });
                        throw new Error("Validation failed");
                    } else if (typeof response.errors === 'string') {
                        // Handle string error
                        toast.error(response.errors);
                        throw new Error(response.errors);
                    }
                }

                throw new Error(response?.message || 'Failed to update contract');
            }
        } catch (error: any) {
            console.error('Error updating contract:', error);
            toast.error(`Failed to update contract: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteContract = async (contractId: number) => {
        try {
            setIsLoading(true);

            // Call the API to delete the contract
            const response = await apiDeleteContract(contractId);

            if (response && response.success) {
                // Remove the contract from the state
                setContracts(prev => prev.filter(contract => contract._id !== contractId));
                toast.success("Contract deleted successfully");
            } else {
                if (response?.errors) {
                    // Handle validation errors
                    if (typeof response.errors === 'object' && response.errors !== null) {
                        // Extract error messages from the object
                        const errorMessages: string[] = [];
                        Object.entries(response.errors).forEach(([field, messages]) => {
                            if (Array.isArray(messages)) {
                                messages.forEach(message => {
                                    errorMessages.push(`${field}: ${message}`);
                                });
                            } else if (typeof messages === 'string') {
                                errorMessages.push(`${field}: ${messages}`);
                            }
                        });

                        if (errorMessages.length > 0) {
                            // Show each validation error as a separate toast
                            errorMessages.forEach(message => {
                                toast.error(message);
                            });
                        } else {
                            // If not successful, show error message but don't refresh data
                            toast.error(response.message || "Failed to delete contract");
                        }
                    } else if (Array.isArray(response.errors)) {
                        // Handle array of errors
                        response.errors.forEach(message => {
                            toast.error(message);
                        });
                    } else if (typeof response.errors === 'string') {
                        // Handle string error
                        toast.error(response.errors);
                    }
                } else {
                    // If not successful, show error message but don't refresh data
                    toast.error(response.message || "Failed to delete contract");
                }
                throw new Error(response?.message || 'Failed to delete contract');
            }
        } catch (error: any) {
            console.error('Error deleting contract:', error);
            toast.error(`Failed to delete contract: ${error.message}`);
            throw error; // Re-throw the error so it can be caught by the component
        } finally {
            setIsLoading(false);
        }
    };

    const generateAgreement = async (contractId: number, isTestSubmission: boolean, isSendWelcomeEmail: boolean, isSendWelcomeEmailDraft: boolean = false, isAttachJointMemberForm: boolean = false) => {
        try {
            setIsLoading(true);

            // Call the API to generate the agreement with all parameters
            const response = await apiGenerateAgreement(
                contractId,
                isTestSubmission,
                isSendWelcomeEmail,
                isSendWelcomeEmailDraft,
                isAttachJointMemberForm
            );

            // Check if the API call was successful
            if (response && response.success) {
                // Update the contract status to active
                setContracts(prev => {
                    return prev.map(contract => {
                        if (contract._id === contractId) {
                            return {
                                ...contract,
                                status: ContractStatus.Active,
                                adobeStatus: AdobeStatus.Waiting
                            };
                        }
                        return contract;
                    });
                });

                toast.success(response.message || "Agreement generated successfully");
                return true;
            } else {
                if (response?.errors) {
                    // Handle validation errors
                    if (typeof response.errors === 'object' && response.errors !== null) {
                        // Extract error messages from the object
                        const errorMessages: string[] = [];
                        Object.entries(response.errors).forEach(([field, messages]) => {
                            if (Array.isArray(messages)) {
                                messages.forEach(message => {
                                    errorMessages.push(`${field}: ${message}`);
                                });
                            } else if (typeof messages === 'string') {
                                errorMessages.push(`${field}: ${messages}`);
                            }
                        });

                        if (errorMessages.length > 0) {
                            // Show each validation error as a separate toast
                            errorMessages.forEach(message => {
                                toast.error(message);
                            });
                        } else {
                            // If not successful, show error message but don't refresh data
                            toast.error(response.message || "Failed to generate agreement");
                        }
                    } else if (Array.isArray(response.errors)) {
                        // Handle array of errors
                        response.errors.forEach(message => {
                            toast.error(message);
                        });
                    } else if (typeof response.errors === 'string') {
                        // Handle string error
                        toast.error(response.errors);
                    }
                } else {
                    // If not successful, show error message but don't refresh data
                    toast.error(response.message || "Failed to generate agreement");
                }
                return false;
            }
        } catch (error: any) {
            console.error('Error generating agreement:', error);
            toast.error(error.message || "Failed to generate agreement");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const updateContractStatus = (contractId: number, status: string, updatedData?: Partial<Contract>) => {
        setContracts(prev => {
            const updated = prev.map(contract => {
                if (contract._id === contractId) {
                    // Apply status update immediately
                    return {
                        ...contract,
                        ...updatedData,
                        status: status
                    };
                }
                return contract;
            });

            return updated;
        });
    };

    const updateContractBookingStatus = (contractId: number, bookingId: number, status: string) => {
        setContracts(prev => {
            const updated = prev.map(contract => {
                if (contract._id === contractId) {
                    // Filter out empty bookings and update status for matching booking
                    const updatedBookings = (contract.bookings || [])
                        .filter(booking => booking && Object.keys(booking).length > 0)
                        .map(booking =>
                            booking._id === bookingId
                                ? { ...booking, status, contractId }
                                : booking
                        );

                    // If contract is already active or completed, maintain those statuses
                    if (contract.status === 'active' || contract.status === 'completed') {
                        return {
                            ...contract,
                            bookings: updatedBookings,
                            status: contract.status
                        };
                    }

                    // Check if contract has any valid bookings or isNoBookingRequired is true
                    const hasBookings = updatedBookings.length > 0;

                    return {
                        ...contract,
                        bookings: updatedBookings,
                        status: contract.isNoBookingRequired || hasBookings ? ContractStatus.Pending : ContractStatus.New
                    };
                }
                return contract;
            });

            return updated;
        });
    };

    const updateContractBookingsStatus = (contractId: number, status: string) => {
        setContracts(prev => {
            const updated = prev.map(contract => {
                if (contract._id === contractId) {
                    // Filter out empty bookings and update status for all bookings
                    const updatedBookings = (contract.bookings || [])
                        .filter(booking => booking && Object.keys(booking).length > 0)
                        .map(booking => ({
                            ...booking,
                            status,
                            contractId
                        }));

                    // If contract is already active or completed, maintain those statuses
                    if (contract.status === 'active' || contract.status === 'completed') {
                        return {
                            ...contract,
                            bookings: updatedBookings,
                            status: contract.status
                        };
                    }

                    // Check if contract has any valid bookings or isNoBookingRequired is true
                    const hasBookings = updatedBookings.length > 0;
                
                    return {
                        ...contract,
                        bookings: updatedBookings,
                        status: contract.isNoBookingRequired || hasBookings ? ContractStatus.Pending : ContractStatus.New
                    };
                }
                return contract;
            });

            return updated;
        });
    };

    const removeBooking = (contractId: number, bookingId: number) => {
        setContracts(prev => {
            const updated = prev.map(contract => {
                if (contract._id === contractId) {
                    // Filter out the booking to remove and any empty bookings
                    const updatedBookings = (contract.bookings || [])
                        .filter(booking =>
                            booking &&
                            Object.keys(booking).length > 0 &&
                            booking._id !== bookingId
                        );

                    // If contract is already active or completed, maintain those statuses
                    if (contract.status === 'active' || contract.status === 'completed') {
                        return {
                            ...contract,
                            bookings: updatedBookings,
                            status: contract.status
                        };
                    }

                    // Check if contract has any valid bookings or isNoBookingRequired is true
                    const hasBookings = updatedBookings.length > 0;
                
                    return {
                        ...contract,
                        bookings: updatedBookings,
                        status: contract.isNoBookingRequired || hasBookings ? ContractStatus.Pending : ContractStatus.New
                    };
                }
                return contract;
            });

            return updated;
        });
    };

    const contextValue = useMemo(() => ({
        contracts,
        pagination,
        addContract,
        updateContract,
        updateContractStatus,
        updateContractBookingStatus,
        updateContractBookingsStatus,
        removeBooking,
        deleteContract,
        fetchContracts,
        generateAgreement,
        isLoading
    }), [contracts, pagination, fetchContracts, isLoading]);

    return (
        <ContractContext.Provider value={contextValue}>
            {children}
        </ContractContext.Provider>
    );
}

export const useContracts = () => {
    const context = useContext(ContractContext);
    if (!context) throw new Error('useContracts must be used within a ContractProvider');
    return context;
};
